import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Filter,
    Calendar,
    DollarSign,
    TrendingUp,
    TrendingDown,
    X,
    Clock,
    ArrowDownUp,
    ChevronDown,
    ChevronUp,
    CreditCard,
    RefreshCw,
    Tag,
} from "lucide-react";
import Title from "../../components/title/Title.tsx";
import { Account, Trade, initialAccounts } from "../../interfaces/accountData";
import "./Trades.css";

interface FilterState {
    dateFrom: string;
    dateTo: string;
    stockName: string;
    sizeMin: string;
    sizeMax: string;
    priceMin: string;
    priceMax: string;
    action: "all" | "buy" | "sell";
}

type SortableField = "timestamp" | "stockName" | "size" | "price" | "expires" | "type" | "action";

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const formatExpiresDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric"
    }).format(date);
};

const Trades: React.FC = () => {
    const { accountId } = useParams<{ accountId: string }>();
    const [account, setAccount] = useState<Account | undefined>(undefined);
    const [trades, setTrades] = useState<Trade[]>([]);
    const [filteredTrades, setFilteredTrades] = useState<Trade[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortField, setSortField] = useState<SortableField>("timestamp");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

    const [filters, setFilters] = useState<FilterState>({
        dateFrom: "",
        dateTo: "",
        stockName: "",
        sizeMin: "",
        sizeMax: "",
        priceMin: "",
        priceMax: "",
        action: "all",
    });

    const fetchTrades = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get("/accounttrades", { params: { accountId, count: 50 } });
            // Map the API response to your Trade interface.
            const apiTrades = response.data.data ?? response.data;
            const mappedTrades = apiTrades.map((t: any) => ({
                id: t.Id ? t.id.toString() : t.id.toString(),
                timestamp: t.timestamp,
                expires: t.expirationDate,
                message: t.message,
                stockName: t.symbol, // assuming the symbol is the stock name
                size: t.quantity,
                action: t.action.toLowerCase(), // converting "BUY"/"SELL" to lowercase
                type: t.type.toLowerCase(), // converting "CALL"/"PUT" to lowercase
                price: t.strikePrice // or use another field if available
            }));
            console.log(mappedTrades);
            setTrades(mappedTrades);
            setFilteredTrades(mappedTrades);
        } catch (error) {
            console.error("Error fetching trades:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchAccountDetails = async () => {
            try {
                const response = await axios.get(`/account/${accountId}`);
                const acc = response.data.data ?? response.data;
                setAccount(acc);
            } catch (error) {
                console.error("Error fetching account:", error);
            }
        };

        if (accountId) {
            setIsLoading(true);
            // Fetch account details and trades concurrently.
            fetchAccountDetails();
            fetchTrades();
        }
    }, [accountId]);


    useEffect(() => {
        applyFilters();
    }, [filters, trades, sortField, sortDirection]);

    const applyFilters = () => {
        let result = [...trades];

        if (filters.dateFrom) {
            result = result.filter(
                (trade) => new Date(trade.timestamp) >= new Date(filters.dateFrom)
            );
        }

        if (filters.dateTo) {
            result = result.filter(
                (trade) => new Date(trade.timestamp) <= new Date(filters.dateTo)
            );
        }

        if (filters.stockName) {
            result = result.filter((trade) =>
                trade.stockName.toLowerCase().includes(filters.stockName.toLowerCase())
            );
        }

        if (filters.sizeMin) {
            result = result.filter(
                (trade) => trade.size >= parseFloat(filters.sizeMin)
            );
        }

        if (filters.sizeMax) {
            result = result.filter(
                (trade) => trade.size <= parseFloat(filters.sizeMax)
            );
        }

        if (filters.priceMin) {
            result = result.filter(
                (trade) => trade.price >= parseFloat(filters.priceMin)
            );
        }

        if (filters.priceMax) {
            result = result.filter(
                (trade) => trade.price <= parseFloat(filters.priceMax)
            );
        }

        if (filters.action !== "all") {
            result = result.filter((trade) => trade.action === filters.action);
        }

        result.sort((a, b) => {
            let comparison = 0;

            switch (sortField) {
                case "timestamp":
                    comparison =
                        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
                    break;
                case "expires":
                    comparison =
                        new Date(a.expires).getTime() - new Date(b.expires).getTime();
                    break;
                case "stockName":
                    comparison = a.stockName.localeCompare(b.stockName);
                    break;
                case "size":
                    comparison = a.size - b.size;
                    break;
                case "price":
                    comparison = a.price - b.price;
                    break;
                case "action":
                    comparison = a.action.localeCompare(b.action);
                    break;
                case "type":
                    comparison = a.type.localeCompare(b.type);
                    break;
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });

        setFilteredTrades(result);
    };

    const handleFilterChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const resetFilters = () => {
        const emptyFilters: FilterState = {
            dateFrom: "",
            dateTo: "",
            stockName: "",
            sizeMin: "",
            sizeMax: "",
            priceMin: "",
            priceMax: "",
            action: "all",
        };

        setFilters(emptyFilters);

        const filterInputs = document.querySelectorAll(".trades-filter-input");
        filterInputs.forEach((input: Element) => {
            if (input instanceof HTMLInputElement) {
                if (
                    input.type === "date" ||
                    input.type === "number" ||
                    input.type === "text"
                ) {
                    input.value = "";
                }
            }
        });

        const actionSelect = document.querySelector('select[name="action"]');
        if (actionSelect instanceof HTMLSelectElement) {
            actionSelect.value = "all";
        }
    };

    const handleSort = (field: SortableField) => {
        if (field === sortField) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const getSortIcon = (field: SortableField) => {
        if (field !== sortField) return <ArrowDownUp size={14} />;
        return sortDirection === "asc" ? (
            <ChevronUp size={14} />
        ) : (
            <ChevronDown size={14} />
        );
    };

    if (isLoading) {
        return (
            <div className="trades-container">
                <div className="trades-loading">
                    <div className="trades-spinner"></div>
                </div>
            </div>
        );
    }

    if (!account) {
        return (
            <div className="trades-container">
                <div className="trades-not-found">
                    <h2>Account not found</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="trades-container">
            <div className="trades-header">
                <div className="trades-header-left">
                    <Title text={`${account.name} Trades`} size="xl" align="left" />
                </div>
                <div className="trades-header-right">
                    <button
                        className={`trades-filter-toggle ${isFilterOpen ? "active" : ""}`}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                    >
                        {isFilterOpen ? <X size={18} /> : <Filter size={18} />}
                        <span>{isFilterOpen ? "Close Filters" : "Show Filters"}</span>
                    </button>
                    <button
                        className="trades-filter-toggle"
                        onClick={fetchTrades}
                    >
                        <RefreshCw size={18} />
                    </button>
                </div>
                
            </div>


            {isFilterOpen && (
                <div className="trades-filters">
                    <div className="trades-filters-header">
                        <h3>Filter Trades</h3>
                        <button className="trades-reset-button" onClick={resetFilters}>
                            <X size={14} />
                            <span>Reset</span>
                        </button>
                    </div>
                    <div className="trades-filters-grid">
                        <div className="trades-filter-group">
                            <label className="trades-filter-label">
                                <Calendar size={14} />
                                <span>Date Range</span>
                            </label>
                            <div className="trades-date-inputs">
                                <input
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="From"
                                />
                                <span className="trades-date-separator">to</span>
                                <input
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="To"
                                />
                            </div>
                        </div>
                        <div className="trades-filter-group">
                            <label className="trades-filter-label">
                                <Tag size={14} />
                                <span>Stock Name</span>
                            </label>
                            <input
                                type="text"
                                name="stockName"
                                value={filters.stockName}
                                onChange={handleFilterChange}
                                className="trades-filter-input"
                                placeholder="Search stocks..."
                            />
                        </div>
                        <div className="trades-filter-group">
                            <label className="trades-filter-label">
                                <CreditCard size={14} />
                                <span>Size Range</span>
                            </label>
                            <div className="trades-range-inputs">
                                <input
                                    type="number"
                                    name="sizeMin"
                                    value={filters.sizeMin}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="Min"
                                    min="0"
                                />
                                <span className="trades-range-separator">to</span>
                                <input
                                    type="number"
                                    name="sizeMax"
                                    value={filters.sizeMax}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="Max"
                                    min="0"
                                />
                            </div>
                        </div>
                        <div className="trades-filter-group">
                            <label className="trades-filter-label">
                                <DollarSign size={14} />
                                <span>Strike Price Range</span>
                            </label>
                            <div className="trades-range-inputs">
                                <input
                                    type="number"
                                    name="priceMin"
                                    value={filters.priceMin}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="Min $"
                                    min="0"
                                    step="0.01"
                                />
                                <span className="trades-range-separator">to</span>
                                <input
                                    type="number"
                                    name="priceMax"
                                    value={filters.priceMax}
                                    onChange={handleFilterChange}
                                    className="trades-filter-input"
                                    placeholder="Max $"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>
                        <div className="trades-filter-group">
                            <label className="trades-filter-label">
                                {filters.action === "buy" ? (
                                    <TrendingUp size={14} />
                                ) : filters.action === "sell" ? (
                                    <TrendingDown size={14} />
                                ) : (
                                    <ArrowDownUp size={14} />
                                )}
                                <span>Trade Action</span>
                            </label>
                            <div className="trades-select-wrapper">
                                <select
                                    name="action"
                                    value={filters.action}
                                    onChange={handleFilterChange}
                                    className="trades-filter-select"
                                >
                                    <option value="all">All Actions</option>
                                    <option value="buy">Buy</option>
                                    <option value="sell">Sell</option>
                                </select>
                                <ChevronDown size={14} className="trades-select-icon" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="trades-content">
                {filteredTrades.length > 0 ? (
                    <>
                        <div className="trades-count">
                            Showing <span>{filteredTrades.length}</span> trades
                        </div>
                        <div className="trades-table-container">
                            <table className="trades-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort("timestamp")}>
                                            <div className="trades-th-content">
                                                <Clock size={14} />
                                                <span>Time</span>
                                                {getSortIcon("timestamp")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("stockName")}>
                                            <div className="trades-th-content">
                                                <Tag size={14} />
                                                <span>Symbol</span>
                                                {getSortIcon("stockName")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("size")}>
                                            <div className="trades-th-content">
                                                <CreditCard size={14} />
                                                <span>Size</span>
                                                {getSortIcon("size")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("price")}>
                                            <div className="trades-th-content">
                                                <DollarSign size={14} />
                                                <span>Strike Price</span>
                                                {getSortIcon("price")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("expires")}>
                                            <div className="trades-th-content">
                                                <Clock size={14} />
                                                <span>Expires</span>
                                                {getSortIcon("expires")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("type")}>
                                            <div className="trades-th-content">
                                                <ArrowDownUp size={14} />
                                                <span>Type</span>
                                                {getSortIcon("type")}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort("action")}>
                                            <div className="trades-th-content">
                                                <ArrowDownUp size={14} />
                                                <span>Action</span>
                                                {getSortIcon("action")}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTrades.map((trade, index) => (
                                        <tr
                                            key={`${trade.id}-${index}`}
                                            className="trades-row"
                                            onClick={() => alert(`Discord message for trade with Id ${trade.id}:\n${trade.message}`)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td className="trades-td-timestamp">
                                                {formatDate(trade.timestamp)}
                                            </td>
                                            <td className="trades-td-stock">
                                                <span className="trades-stock-symbol">
                                                    {trade.stockName}
                                                </span>
                                            </td>
                                            <td className="trades-td-size">{trade.size}</td>
                                            <td className="trades-td-price">
                                                ${trade.price.toFixed(2)}
                                            </td>
                                            <td className="trades-td-timestamp">
                                                {formatExpiresDate(trade.expires)}
                                            </td>
                                            <td className="trades-td-timestamp">
                                                {trade.type.toUpperCase()}
                                            </td>
                                            <td className="trades-td-action">
                                                <div
                                                    className={`trades-action-badge ${trade.action === "buy" ? "buy" : "sell"
                                                        }`}
                                                >
                                                    {trade.action === "buy" ? (
                                                        <>
                                                            <TrendingUp size={14} />
                                                            <span>Buy</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <TrendingDown size={14} />
                                                            <span>Sell</span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <div className="trades-empty-state">
                        <TrendingUp size={36} />
                        <h3>No trades found</h3>
                        <p>Try adjusting your filters or check back later</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Trades;
